import { Card } from 'antd';
import * as eva from 'eva-icons';
import { filter, map } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import {
  BREAKPOINTS,
  FIELD_TYPES,
  TIME_12_HOUR_FORMAT,
  TIME_24_HOUR_FORMAT,
} from '../../common/constants';
import AttachmentViewer from '../../components/AttachmentViewer';
import CommonTable from '../../components/CommonTable';
import EllipsisText from '../../components/EllipsisText';
import RegisterCommentModal from './RegisterCommentModal';
import RegisterCommentPopUp from './RegisterCommentPopUp';

const RegisterLogEntriesSection = ({
  data,
  refetch,
  setCommentData,
  commentData,
  loading,
  onScroll,
}) => {
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  const registerFormFields = [
    ...(data?.register?.registerFormFields || []),
  ]?.sort((a, b) => a?.sequence - b?.sequence);
  const registerFormFieldEntries =
    data?.registerLog?.registerFormFieldEntries || [];

  useEffect(() => {
    eva.replace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFormFieldEntries]);

  const getFieldValue = (fieldId, rowIndex, field) => {
    const entry = registerFormFieldEntries?.find(
      (e) => e?.registerFormFieldId === fieldId && e?.rowIndex === rowIndex,
    );

    // Check if this is an ATTACHMENT type field
    if (field?.type === FIELD_TYPES.ATTACHMENT && entry?.responses) {
      return <AttachmentViewer attachments={entry?.responses} />;
    }

    const value = entry?.value;
    // Convert 24-hour time format to 12-hour format
    if (moment(value, TIME_24_HOUR_FORMAT, true)?.isValid()) {
      return (
        <EllipsisText
          text={moment(value, TIME_24_HOUR_FORMAT).format(TIME_12_HOUR_FORMAT)}
        />
      );
    }

    return <EllipsisText text={value || '-'} />;
  };

  const handleAddComment = (field, rowIndex) => {
    setSelectedField(field);
    setSelectedRowIndex(rowIndex);
    setShowCommentModal(true);
  };

  // Create table columns dynamically based on registerFormFields
  const columns = [
    {
      title: '#',
      dataIndex: 'rowIndex',
      key: 'rowIndex',
      width: 80,
      render: (text) => text + 1,
    },
    ...map(registerFormFields, (field) => ({
      title: field?.name,
      key: field?.id,
      render: (text, record) =>
        getFieldValue(field?.id, record?.rowIndex, field),
    })),
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'end',
      render: (text, record) => {
        const hasComment = commentData?.comments?.find(
          (c) =>
            c?.rowIndex === record?.rowIndex && c?.fieldType === 'formField',
        );
        const previousComments = filter(
          data?.registerLog?.registerFormFieldEntries,
          (item) => item?.id === record?.id,
        );

        return (
          <div>
            <RegisterCommentPopUp
              previousHistoryData={previousComments}
              fieldData={{ id: record?.id, name: record?.id }}
              data={data}
              onClick={() => {
                handleAddComment(
                  {
                    id: record?.id,
                    name: record?.id,
                  },
                  record?.rowIndex,
                );
              }}
              hasComment={!!hasComment}
              fieldType="formField"
              rowIndex={record?.rowIndex}
            />
          </div>
        );
      },
    },
  ];

  // Group entries by rowIndex to create proper rows
  const groupedByRow = registerFormFieldEntries?.reduce((acc, entry) => {
    const { rowIndex } = entry;
    if (!acc[rowIndex]) {
      acc[rowIndex] = { rowIndex, entries: [] };
    }
    acc[rowIndex].entries.push(entry);
    return acc;
  }, {});

  // Create dataSource with unique rows
  const dataSource = map(Object.values(groupedByRow || {}), (rowData) => ({
    key: `row-${rowData?.rowIndex}`,
    id: rowData?.entries?.[0]?.id,
    rowIndex: rowData?.rowIndex,
  }));
  // Render mobile card view for each entry
  const renderMobileEntryCard = (rowData, index) => {
    const hasComment = commentData?.comments?.find(
      (c) => c?.rowIndex === rowData?.rowIndex && c?.fieldType === 'formField',
    );
    const previousComments = filter(
      data?.registerLog?.registerFormFieldEntries,
      (item) => item?.id === rowData?.id,
    );

    return (
      <Card
        key={rowData?.rowIndex}
        className="mb-16 register-entry-mobile-card"
      >
        <div className="mb-16">
          <h3 className="mb-16" style={{ fontSize: '18px', fontWeight: '600' }}>
            Row #{index + 1}
          </h3>
          <div className="register-entry-fields">
            {map(registerFormFields, (field) => (
              <div key={field?.id} className="entry-field-item mb-12">
                <div
                  className="entry-field-label"
                  style={{
                    color: '#8c8c8c',
                    fontSize: '13px',
                    marginBottom: '4px',
                    fontWeight: '500',
                  }}
                >
                  {field?.name}:
                </div>
                <div
                  className="entry-field-value"
                  style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#262626',
                  }}
                >
                  {getFieldValue(field?.id, rowData?.rowIndex, field)}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="d-flex justify-center">
          <RegisterCommentPopUp
            previousHistoryData={previousComments}
            fieldData={{ id: rowData?.id, name: rowData?.id }}
            data={data}
            onClick={() =>
              handleAddComment(
                { id: rowData?.id, name: rowData?.id },
                rowData?.rowIndex,
              )
            }
            hasComment={!!hasComment}
            fieldType="formField"
            rowIndex={rowData?.rowIndex}
          />
        </div>
      </Card>
    );
  };

  return (
    <>
      {showCommentModal && selectedField && (
        <RegisterCommentModal
          showModal={showCommentModal}
          setShowModal={setShowCommentModal}
          data={data}
          fieldData={selectedField}
          setCommentData={setCommentData}
          commentData={commentData}
          fieldType="formField"
          rowIndex={selectedRowIndex}
          refetch={refetch}
        />
      )}

      <div className="approver-stage-details">
        {registerFormFieldEntries?.length > 0 && (
          <div className="approver-stage-details-header d-flex">
            <i data-eva="list-outline" className="mr-16" />
            <h2>Register Entries</h2>
          </div>
        )}
        {registerFormFieldEntries?.length > 0 ? (
          <>
            {isDesktopViewport ? (
              <Card
                loading={loading}
                className="mb-24 register-scrollable-table-card"
              >
                <CommonTable
                  columns={columns}
                  dataSource={dataSource}
                  rowKey={(obj) => obj?.id}
                  scroll={{
                    y: 500,
                  }}
                  onScroll={onScroll}
                />
              </Card>
            ) : (
              <div
                className="register-mobile-scroll-wrapper mb-24"
                onScroll={onScroll}
              >
                {loading && dataSource?.length === 0 ? (
                  <Card loading={loading} />
                ) : (
                  map(dataSource, (row, index) =>
                    renderMobileEntryCard(row, index),
                  )
                )}
              </div>
            )}
          </>
        ) : (
          <Card loading={loading} className="mb-24">
            <div className="text-center text-secondary">No entries found</div>
          </Card>
        )}
      </div>
    </>
  );
};

export default RegisterLogEntriesSection;
